/**
 * Unified attributes — the cross-app section of Settings → Field Mappings.
 *
 * Grows the Semantic Maps surface with the concept doc 07 names: one canonical
 * attribute, bound to how each connected app expresses it, so a change anywhere
 * flows to the canonical item and (for bidirectional attributes) is queued back
 * out. Lives on the Field Mappings page rather than a page of its own — the
 * set's standing constraint: no second mapping UI.
 *
 * Gate: semantic_connectors module (the page mounts this only when
 * useSemanticConnectorsEnabled() is true).
 * Spec: docs/9-exploration/semantic-connectors/07-VISION-attribute-unification.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  Check,
  Link2,
  Loader2,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { Callout, EmptyState, Field, Modal, Select, TextInput } from '../../../components/ds';
import { Button } from '../../../components/ui/Button';
import { CONNECTOR_FIELDS, getAllConnectors, INTERNAL_FIELDS } from '../../../lib/fieldMappingRegistry';
import {
  CONFLICT_POLICY_LABELS,
  OUTBOUND_TRANSPORT,
  PROPAGATION_MODE_LABELS,
  unifiableInternalFields,
  type BindingSourceKind,
  type PropagationStatus,
  type UnifiedAttributeBinding,
  type UnifiedAttributeWithBindings,
  type UnifiedConflictPolicy,
  type UnifiedPropagationMode,
  type ValueMapEntry,
} from '../../../lib/semanticConnectors/unifiedAttributes';
import {
  countPropagations,
  createBinding,
  createUnifiedAttribute,
  deleteBinding,
  deleteUnifiedAttribute,
  listObservedSourceKeys,
  listPropagations,
  listUnifiedAttributes,
  resolveReview,
  runOutboundQueue,
  updateBinding,
  updateUnifiedAttribute,
  type PropagationRecord,
} from '../../../lib/semanticConnectors/unifiedAttributesService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const SIGNIFIER_KEYS = ['fill_color', 'shape_color', 'border_color', 'text_color', 'shape'] as const;
const CUSTOM_KEY = '__custom__';

function fieldLabel(key: string): string {
  return INTERNAL_FIELDS.find((f) => f.key === key)?.label ?? key;
}

function connectorLabel(type: string): string {
  return getAllConnectors().find((c) => c.type === type)?.label ?? type;
}

const STATUS_TONE: Record<PropagationStatus, string> = {
  applied: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  dispatched: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  queued: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  review: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  skipped: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
  dismissed: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
  failed: 'bg-[var(--coral-dim)] text-[var(--coral)]',
};

function StatusChip({ status }: { status: PropagationStatus }) {
  return (
    <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_TONE[status]}`}>
      {status}
    </span>
  );
}

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'string' ? v : JSON.stringify(v);
}


// ── Attribute editor ──────────────────────────────────────────────────────────

interface AttributeForm {
  name: string;
  canonicalFieldKey: string;
  customKey: string;
  propagation: UnifiedPropagationMode;
  conflictPolicy: UnifiedConflictPolicy;
  description: string;
}

function AttributeModal({
  initial,
  onSave,
  onClose,
}: {
  initial: AttributeForm;
  onSave: (form: AttributeForm) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fields = useMemo(() => unifiableInternalFields(), []);
  const resolvedKey = form.canonicalFieldKey === CUSTOM_KEY ? `attribute:${form.customKey.trim()}` : form.canonicalFieldKey;
  const valid = form.name.trim() && (form.canonicalFieldKey !== CUSTOM_KEY || form.customKey.trim());

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ ...form, canonicalFieldKey: resolvedKey });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={initial.name ? 'Edit unified attribute' : 'New unified attribute'}
      label="Unified attribute"
      description="One canonical attribute that connected apps are declared to share."
      footer={
        <>
          <Button onClick={onClose} variant="secondary" size="sm">Cancel</Button>
          <Button onClick={() => void submit()} disabled={saving || !valid} variant="primary" size="sm">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Impact" autoFocus />
        </Field>
        <Field label="Canonical field" required hint="The Priorities.ai field every binding reads into and writes from.">
          <Select
            value={form.canonicalFieldKey}
            onChange={(e) => setForm({ ...form, canonicalFieldKey: e.target.value })}
            options={[
              ...fields.map((f) => ({ value: f.key, label: f.label })),
              { value: CUSTOM_KEY, label: 'Custom attribute key…' },
            ]}
          />
        </Field>
        {form.canonicalFieldKey === CUSTOM_KEY && (
          <Field label="Attribute key" required hint="Stored as metadata['attribute:<key>'] on the item.">
            <TextInput value={form.customKey} onChange={(e) => setForm({ ...form, customKey: e.target.value })} placeholder="impact" />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Propagation" hint="Bidirectional also queues canonical changes back out.">
            <Select
              value={form.propagation}
              onChange={(e) => setForm({ ...form, propagation: e.target.value as UnifiedPropagationMode })}
              options={(Object.keys(PROPAGATION_MODE_LABELS) as UnifiedPropagationMode[]).map((k) => ({ value: k, label: PROPAGATION_MODE_LABELS[k] }))}
            />
          </Field>
          <Field label="On conflict" hint="When an app's value differs from a canonical value already set.">
            <Select
              value={form.conflictPolicy}
              onChange={(e) => setForm({ ...form, conflictPolicy: e.target.value as UnifiedConflictPolicy })}
              options={(Object.keys(CONFLICT_POLICY_LABELS) as UnifiedConflictPolicy[]).map((k) => ({ value: k, label: CONFLICT_POLICY_LABELS[k] }))}
            />
          </Field>
        </div>
        <Field label="Description">
          <TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Why these are the same thing" />
        </Field>
        {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
      </div>
    </Modal>
  );
}

// ── Binding editor ────────────────────────────────────────────────────────────

interface BindingForm {
  connectorType: string;
  sourceKind: BindingSourceKind;
  sourceKey: string;
  customSourceKey: string;
  valueMap: ValueMapEntry[];
  outboundDefaults: Record<string, string>;
  enabled: boolean;
}

function BindingModal({
  workspaceId,
  canonicalFieldKey,
  initial,
  onSave,
  onClose,
}: {
  workspaceId: string;
  canonicalFieldKey: string;
  initial: BindingForm;
  onSave: (form: BindingForm) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [observed, setObserved] = useState<Array<{ sourceKind: BindingSourceKind; sourceKey: string; sampleValues: string[] }>>([]);
  const connectors = useMemo(() => getAllConnectors().filter((c) => c.type !== 'csv'), []);

  useEffect(() => {
    listObservedSourceKeys(workspaceId, form.connectorType).then(setObserved).catch(() => setObserved([]));
  }, [workspaceId, form.connectorType]);

  const keyOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ value: string; label: string }> = [];
    const push = (value: string, label: string) => {
      if (seen.has(value)) return;
      seen.add(value);
      out.push({ value, label });
    };
    if (form.sourceKind === 'signifier') {
      for (const k of SIGNIFIER_KEYS) push(k, k);
    } else {
      push('title', 'Title (content)');
      push('description', 'Description (content)');
      for (const f of CONNECTOR_FIELDS[form.connectorType] ?? []) push(f.key, `${f.label} (${f.key})`);
    }
    for (const o of observed) {
      if (o.sourceKind === form.sourceKind) push(o.sourceKey, `${o.sourceKey} · seen: ${o.sampleValues.slice(0, 3).join(', ') || '—'}`);
    }
    push(CUSTOM_KEY, 'Other key…');
    return out;
  }, [form.sourceKind, form.connectorType, observed]);

  const samples = observed.find((o) => o.sourceKind === form.sourceKind && o.sourceKey === form.sourceKey)?.sampleValues ?? [];

  // Internal values that map from more than one external value need an outbound default.
  const ambiguous = useMemo(() => {
    const byInternal = new Map<string, string[]>();
    for (const e of form.valueMap) {
      const k = e.internalValue.trim().toLowerCase();
      if (!k) continue;
      byInternal.set(k, [...(byInternal.get(k) ?? []), e.externalValue]);
    }
    return [...byInternal.entries()].filter(([, ext]) => ext.length > 1);
  }, [form.valueMap]);

  const carriable = OUTBOUND_TRANSPORT[form.connectorType]?.has(canonicalFieldKey) ?? false;
  const resolvedKey = form.sourceKey === CUSTOM_KEY ? form.customSourceKey.trim() : form.sourceKey;
  const valid = !!form.connectorType && !!resolvedKey;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...form,
        sourceKey: resolvedKey,
        valueMap: form.valueMap.filter((e) => e.externalValue.trim() && e.internalValue.trim()),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={initial.sourceKey ? 'Edit binding' : 'Bind a connected app'}
      label={fieldLabel(canonicalFieldKey)}
      description="How this app expresses the attribute, and how its values translate in each direction."
      size="lg"
      footer={
        <>
          <Button onClick={onClose} variant="secondary" size="sm">Cancel</Button>
          <Button onClick={() => void submit()} disabled={saving || !valid} variant="primary" size="sm">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save binding
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Connected app" required>
            <Select
              value={form.connectorType}
              onChange={(e) => setForm({ ...form, connectorType: e.target.value, sourceKey: '' })}
              options={connectors.map((c) => ({ value: c.type, label: c.label }))}
            />
          </Field>
          <Field label="Source kind" hint="Signifier = a visual channel; field = a named property.">
            <Select
              value={form.sourceKind}
              onChange={(e) => setForm({ ...form, sourceKind: e.target.value as BindingSourceKind, sourceKey: '' })}
              options={[
                { value: 'signifier', label: 'Signifier (colour, shape)' },
                { value: 'field', label: 'Field / property' },
              ]}
            />
          </Field>
          <Field label="Source" required>
            <Select
              value={form.sourceKey}
              onChange={(e) => setForm({ ...form, sourceKey: e.target.value })}
              placeholder="Pick a source"
              options={keyOptions}
            />
          </Field>
        </div>
        {form.sourceKey === CUSTOM_KEY && (
          <Field label="Source key" required>
            <TextInput value={form.customSourceKey} onChange={(e) => setForm({ ...form, customSourceKey: e.target.value })} placeholder="e.g. Priority" />
          </Field>
        )}
        {samples.length > 0 && (
          <p className="text-xs text-[var(--text-ter)]">
            Values seen so far from {connectorLabel(form.connectorType)}: {samples.join(', ')}
          </p>
        )}

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-[var(--text-sec)]">Value map</span>
            <Button
              variant="ghost" size="sm" className="!text-xs font-medium text-primary-600 hover:underline inline-flex items-center !gap-1"
              onClick={() => setForm({ ...form, valueMap: [...form.valueMap, { externalValue: '', internalValue: '' }] })}
            >
              <Plus className="h-3 w-3" /> Add row
            </Button>
          </div>
          <p className="text-[11px] text-[var(--text-ter)] mb-2">
            Empty map = values pass through unchanged. Several app values may map to one canonical value (many-to-one inbound is fine).
          </p>
          {form.valueMap.length === 0 ? (
            <p className="text-xs text-[var(--text-ter)] border border-dashed border-[var(--rule)] px-3 py-2">Pass-through.</p>
          ) : (
            <div className="space-y-1.5">
              {form.valueMap.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <TextInput
                    size="sm"
                    value={row.externalValue}
                    placeholder={`${connectorLabel(form.connectorType)} value`}
                    onChange={(e) => {
                      const next = form.valueMap.slice();
                      next[i] = { ...row, externalValue: e.target.value };
                      setForm({ ...form, valueMap: next });
                    }}
                  />
                  <ArrowLeftRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                  <TextInput
                    size="sm"
                    value={row.internalValue}
                    placeholder="canonical value"
                    onChange={(e) => {
                      const next = form.valueMap.slice();
                      next[i] = { ...row, internalValue: e.target.value };
                      setForm({ ...form, valueMap: next });
                    }}
                  />
                  <Button
                    aria-label="Remove row"
                    variant="ghost" size="sm" className="!p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                    onClick={() => setForm({ ...form, valueMap: form.valueMap.filter((_, j) => j !== i) })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {ambiguous.length > 0 && (
          <div>
            <span className="text-xs font-medium text-[var(--text-sec)]">Outbound defaults</span>
            <p className="text-[11px] text-[var(--text-ter)] mb-2">
              These canonical values map to several {connectorLabel(form.connectorType)} values. Declare which one to write
              on the way out — otherwise the hop is recorded as lossy and skipped.
            </p>
            <div className="space-y-1.5">
              {ambiguous.map(([internal, externals]) => (
                <div key={internal} className="flex items-center gap-2 text-xs">
                  <span className="w-32 truncate font-medium text-[var(--text-pri)]">{internal}</span>
                  <ArrowUpFromLine className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                  <Select
                    size="sm"
                    value={form.outboundDefaults[internal] ?? ''}
                    placeholder="— no default (lossy) —"
                    onChange={(e) => setForm({ ...form, outboundDefaults: { ...form.outboundDefaults, [internal]: e.target.value } })}
                    options={externals.map((x) => ({ value: x, label: x }))}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <Callout tone={carriable ? 'green' : 'mustard'} label="Outbound fidelity">
          {carriable
            ? `The ${connectorLabel(form.connectorType)} sync transport can carry "${fieldLabel(canonicalFieldKey)}" outbound.`
            : `The ${connectorLabel(form.connectorType)} transport cannot carry "${fieldLabel(canonicalFieldKey)}" outbound yet — inbound still applies; outbound hops are recorded as skipped with this reason, never dropped silently.`}
        </Callout>
        {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
      </div>
    </Modal>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

export function UnifiedAttributesSection({ workspaceId }: { workspaceId: string | null }) {
  const [attributes, setAttributes] = useState<UnifiedAttributeWithBindings[]>([]);
  const [ledger, setLedger] = useState<PropagationRecord[]>([]);
  const [counts, setCounts] = useState({ queued: 0, review: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [attrModal, setAttrModal] = useState<{ id: string | null; form: AttributeForm } | null>(null);
  const [bindingModal, setBindingModal] = useState<{ attribute: UnifiedAttributeWithBindings; binding: UnifiedAttributeBinding | null } | null>(null);
  const [showLedger, setShowLedger] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [attrs, rows, c] = await Promise.all([
        listUnifiedAttributes(workspaceId, { includeInactive: true }),
        listPropagations(workspaceId, { limit: 30 }),
        countPropagations(workspaceId),
      ]);
      setAttributes(attrs);
      setLedger(rows);
      setCounts(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!workspaceId) return null;

  const openNewAttribute = () =>
    setAttrModal({
      id: null,
      form: { name: '', canonicalFieldKey: 'attribute:importance', customKey: '', propagation: 'inbound', conflictPolicy: 'source_wins', description: '' },
    });

  const openEditAttribute = (a: UnifiedAttributeWithBindings) =>
    setAttrModal({
      id: a.id,
      form: {
        name: a.name,
        canonicalFieldKey: unifiableInternalFields().some((f) => f.key === a.canonicalFieldKey) ? a.canonicalFieldKey : CUSTOM_KEY,
        customKey: a.canonicalFieldKey.replace(/^attribute:/, ''),
        propagation: a.propagation,
        conflictPolicy: a.conflictPolicy,
        description: a.description ?? '',
      },
    });

  const saveAttribute = async (form: AttributeForm) => {
    const input = {
      name: form.name,
      canonicalFieldKey: form.canonicalFieldKey,
      description: form.description,
      propagation: form.propagation,
      conflictPolicy: form.conflictPolicy,
    };
    if (attrModal?.id) await updateUnifiedAttribute(attrModal.id, input);
    else await createUnifiedAttribute(workspaceId, input);
    await load();
  };

  const removeAttribute = async (a: UnifiedAttributeWithBindings) => {
    if (!confirm(`Delete unified attribute "${a.name}" and its ${a.bindings.length} binding(s)? Values already written to items are kept.`)) return;
    try {
      await deleteUnifiedAttribute(a.id, workspaceId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleActive = async (a: UnifiedAttributeWithBindings) => {
    try {
      await updateUnifiedAttribute(a.id, { active: !a.active });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const saveBinding = async (form: BindingForm) => {
    if (!bindingModal) return;
    const input = {
      connectorType: form.connectorType,
      sourceKind: form.sourceKind,
      sourceKey: form.sourceKey,
      valueMap: form.valueMap,
      outboundDefaults: form.outboundDefaults,
      enabled: form.enabled,
    };
    if (bindingModal.binding) await updateBinding(bindingModal.binding.id, workspaceId, input);
    else await createBinding(workspaceId, bindingModal.attribute.id, input);
    await load();
  };

  const removeBinding = async (b: UnifiedAttributeBinding) => {
    try {
      await deleteBinding(b.id, workspaceId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleBinding = async (b: UnifiedAttributeBinding) => {
    try {
      await updateBinding(b.id, workspaceId, { enabled: !b.enabled });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const runQueue = async () => {
    setRunning(true);
    setNotice(null);
    try {
      const r = await runOutboundQueue(workspaceId);
      setNotice(
        `Outbound run: ${r.dispatched} handed to sync dispatch, ${r.skipped} skipped (see reasons), ${r.failed} failed${
          r.dispatchError ? ` — dispatch: ${r.dispatchError}` : ''
        }.`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  const review = async (row: PropagationRecord, decision: 'apply' | 'dismiss') => {
    try {
      await resolveReview(row, decision);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="bg-[var(--bg-raised)] border border-[var(--rule)]">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[var(--rule)]">
        <div className="flex items-start gap-2 min-w-0">
          <Link2 className="h-4 w-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[var(--text-pri)]">Unified attributes (cross-app)</div>
            <p className="text-xs text-[var(--text-sec)]">
              Declare that a Miro colour, a Notion property and a Jira field are the same canonical attribute.
              Inbound values apply at every import and excursion return; bidirectional attributes queue canonical changes back out.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button onClick={() => void load()} variant="secondary" size="sm">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={openNewAttribute} variant="primary" size="sm">
            <Plus className="h-3.5 w-3.5" /> New unified attribute
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {error && <Callout tone="coral" label="Error">{error}</Callout>}
        {notice && <Callout tone="slate" label="Outbound">{notice}</Callout>}

        {!loading && attributes.length === 0 ? (
          <EmptyState
            size="inline"
            icon={Link2}
            title="Nothing unified yet"
            description="Start with the attribute your boards and trackers all express differently — impact is the canonical example (green sticky = high, Notion select = High, Jira priority = Highest)."
            action={<Button onClick={openNewAttribute} variant="primary" size="sm"><Plus className="h-3.5 w-3.5" /> New unified attribute</Button>}
          />
        ) : (
          <div className="space-y-3">
            {attributes.map((a) => (
              <div key={a.id} className={`border border-[var(--rule)] ${a.active ? '' : 'opacity-60'}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-[var(--surface)]/60">
                  <div className="min-w-0 flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[var(--text-pri)]">{a.name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)]">
                      {fieldLabel(a.canonicalFieldKey)}
                    </span>
                    <span className="text-[11px] text-[var(--text-ter)] inline-flex items-center gap-1">
                      {a.propagation === 'bidirectional' ? <ArrowLeftRight className="h-3 w-3" /> : <ArrowDownToLine className="h-3 w-3" />}
                      {PROPAGATION_MODE_LABELS[a.propagation]} · {CONFLICT_POLICY_LABELS[a.conflictPolicy]}
                    </span>
                    {!a.active && <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--text-dim)]">inactive</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button title="Edit" onClick={() => openEditAttribute(a)} variant="ghost" size="sm" className="!p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button onClick={() => void toggleActive(a)} variant="ghost" size="sm" className="!px-2 !py-1 !text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)]">{a.active ? 'Deactivate' : 'Activate'}</Button>
                    <Button title="Delete" onClick={() => void removeAttribute(a)} variant="ghost" size="sm" className="!p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div className="px-3 py-2 space-y-1.5">
                  {a.bindings.length === 0 ? (
                    <p className="text-xs text-[var(--text-ter)]">No apps bound yet — nothing flows until at least one is.</p>
                  ) : (
                    a.bindings.map((b) => (
                      <div key={b.id} className={`flex flex-wrap items-center justify-between gap-2 text-xs ${b.enabled ? '' : 'opacity-60'}`}>
                        <div className="min-w-0 flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[var(--text-pri)]">{connectorLabel(b.connectorType)}</span>
                          <span className="text-[var(--text-sec)]">{b.sourceKind === 'signifier' ? 'signifier' : 'field'} · <span className="font-mono">{b.sourceKey}</span></span>
                          <span className="text-[var(--text-ter)]">
                            {b.valueMap.length === 0 ? 'pass-through' : `${b.valueMap.length} value${b.valueMap.length === 1 ? '' : 's'} mapped`}
                          </span>
                          {!(OUTBOUND_TRANSPORT[b.connectorType]?.has(a.canonicalFieldKey) ?? false) && a.propagation === 'bidirectional' && (
                            <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)]" title="Outbound hops to this app are recorded as skipped">inbound only in practice</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button onClick={() => setBindingModal({ attribute: a, binding: b })} variant="ghost" size="sm" className="!px-2 !py-1 !text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)]">Edit</Button>
                          <Button onClick={() => void toggleBinding(b)} variant="ghost" size="sm" className="!px-2 !py-1 !text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)]">{b.enabled ? 'Disable' : 'Enable'}</Button>
                          <Button onClick={() => void removeBinding(b)} variant="ghost" size="sm" className="!p-1 text-[var(--text-ter)] hover:text-[var(--coral)]" title="Remove binding"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                    ))
                  )}
                  <Button onClick={() => setBindingModal({ attribute: a, binding: null })} variant="ghost" size="sm" className="mt-1 !text-xs font-medium text-primary-600 hover:underline inline-flex items-center !gap-1">
                    <Plus className="h-3 w-3" /> Bind a connected app
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ledger */}
        <div className="border-t border-[var(--rule)] pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button onClick={() => setShowLedger((v) => !v)} variant="ghost" size="sm" className="!text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)]">
              Propagation ledger {showLedger ? '▾' : '▸'}
              <span className="ml-2 font-mono text-[10px] text-[var(--text-ter)]">{counts.queued} queued · {counts.review} for review</span>
            </Button>
            <Button onClick={() => void runQueue()} disabled={running || counts.queued === 0} variant="secondary" size="sm" title="Hand queued outbound hops to the sync transport now">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Run outbound queue
            </Button>
          </div>
          {showLedger && (
            <div className="mt-2 max-h-80 overflow-auto border border-[var(--rule)]">
              {ledger.length === 0 ? (
                <p className="px-3 py-2 text-xs text-[var(--text-ter)]">No hops recorded yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <tbody>
                    {ledger.map((r) => (
                      <tr key={r.id} className="border-b border-[var(--rule)] last:border-b-0 align-top">
                        <td className="px-2 py-1.5 whitespace-nowrap"><StatusChip status={r.status} /></td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-[var(--text-sec)]">
                          {r.direction === 'inbound' ? <ArrowDownToLine className="inline h-3 w-3 mr-1" /> : <ArrowUpFromLine className="inline h-3 w-3 mr-1" />}
                          {connectorLabel(r.connectorType)}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[var(--text-ter)]">{fieldLabel(r.canonicalFieldKey)}</td>
                        <td className="px-2 py-1.5 text-[var(--text-pri)]">{fmt(r.fromValue)} → {fmt(r.toValue)}</td>
                        <td className="px-2 py-1.5 text-[var(--text-ter)]">{r.reason ?? ''}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-right">
                          {r.status === 'review' ? (
                            <span className="inline-flex gap-1">
                              <Button onClick={() => void review(r, 'apply')} variant="ghost" size="sm" className="!px-2 !py-0.5 !text-[11px] font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:underline">Apply</Button>
                              <Button onClick={() => void review(r, 'dismiss')} variant="ghost" size="sm" className="!px-2 !py-0.5 !text-[11px] text-[var(--text-ter)] hover:underline">Dismiss</Button>
                            </span>
                          ) : (
                            <span className="text-[10px] text-[var(--text-dim)]">{new Date(r.createdAt).toLocaleString()}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {attrModal && (
        <AttributeModal initial={attrModal.form} onSave={saveAttribute} onClose={() => setAttrModal(null)} />
      )}
      {bindingModal && (
        <BindingModal
          workspaceId={workspaceId}
          canonicalFieldKey={bindingModal.attribute.canonicalFieldKey}
          initial={
            bindingModal.binding
              ? {
                  connectorType: bindingModal.binding.connectorType,
                  sourceKind: bindingModal.binding.sourceKind,
                  sourceKey: bindingModal.binding.sourceKey,
                  customSourceKey: '',
                  valueMap: bindingModal.binding.valueMap,
                  outboundDefaults: bindingModal.binding.outboundDefaults,
                  enabled: bindingModal.binding.enabled,
                }
              : { connectorType: 'miro', sourceKind: 'signifier', sourceKey: 'fill_color', customSourceKey: '', valueMap: [], outboundDefaults: {}, enabled: true }
          }
          onSave={saveBinding}
          onClose={() => setBindingModal(null)}
        />
      )}
    </section>
  );
}

export default UnifiedAttributesSection;
